import type { Column } from "@fronterahealth/frontera-ui-components";
import { Label, Table } from "@fronterahealth/frontera-ui-components";

import type { EvalRubricSummary } from "@api/rest/types-and-hooks";
import { useListEvalRubrics } from "@api/rest/types-and-hooks";
import { RubricStatus, STATUS_STYLES } from "@components/EvalRubric/constants";

const formatDate = (value: string | null | undefined): string => (value ? new Date(value).toLocaleDateString() : "—");

/**
 * The version history, read-only.
 *
 * Rows do not open: a criterion is edited in place above, so there is no
 * per-version page to drill into. A retired version is here so the wording a
 * past score was made against stays visible after copy-on-write moved on —
 * so the whole section, heading included, is worth nothing until a version
 * exists. It renders its own heading, or nothing at all.
 */
export const EvalRubricsTable: React.FC = () => {
  const { data, isFetching } = useListEvalRubrics();

  const columns: Column<EvalRubricSummary>[] = [
    {
      dataIndex: "version",
      key: "version",
      title: "Version",
      className: "w-[10%]",
      render: (_: unknown, record: EvalRubricSummary) => (
        <span className="font-medium text-text-primary">v{record.version}</span>
      ),
    },
    {
      dataIndex: "name",
      key: "name",
      title: "Name",
      className: "w-[32%]",
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Status",
      className: "w-[14%]",
      render: (_: unknown, record: EvalRubricSummary) => (
        <span
          className={`inline-flex rounded px-2 py-0.5 text-sm capitalize ${
            STATUS_STYLES[record.status] ?? STATUS_STYLES[RubricStatus.DRAFT]
          }`}
        >
          {record.status}
        </span>
      ),
    },
    {
      dataIndex: "criteria_count",
      key: "criteria_count",
      title: "Criteria",
      className: "w-[10%]",
    },
    {
      dataIndex: "activated_at",
      key: "activated_at",
      title: "Activated",
      className: "w-[16%]",
      render: (_: unknown, record: EvalRubricSummary) => (
        <span className="text-text-secondary">{formatDate(record.activated_at)}</span>
      ),
    },
    {
      dataIndex: "notes",
      key: "notes",
      title: "What changed",
      className: "w-[18%]",
      render: (_: unknown, record: EvalRubricSummary) => (
        <span className="text-text-secondary line-clamp-2">{record.notes || "—"}</span>
      ),
    },
  ];

  // The Orval fetcher resolves on 4xx rather than throwing, so the status
  // has to be checked before reading the body.
  const rubrics = (data?.status === 200 ? data.data.results : undefined) ?? [];

  // Nothing until there is something. An empty-state card here only said
  // "you have not edited anything yet", which the criteria above already make
  // obvious, while taking up half the page. Nothing while fetching either, so
  // the section doesn't flash in and collapse.
  if (rubrics.length === 0) return null;

  return (
    <div>
      <Label size="small" colorType="secondary" className="mb-2 block uppercase tracking-wide">
        History
      </Label>
      <Table<EvalRubricSummary> data={rubrics} columns={columns} loading={isFetching} />
    </div>
  );
};
